import { Composable, Inject, Vue } from '@natureos/vue-facing-decorator';

import type { ApiResponse, ApiService } from '@/common/services/api.service-new';

import type {
  ClimateDatasetsResponse,
  ClimateTimeseriesRequest,
  ClimateTimeseriesResponse,
} from './climate.types';

/**
 * Service wrapper for the analytics-side climate endpoint:
 * `POST /climate/timeseries`, live in dev, stage and prod.
 *
 * `POST /era5/timeseries` remains a real 200 alias on the same handler for one
 * release, but it shares that handler's validation — so it protects the *path*,
 * not the *vocabulary*. Variable names must be the ones the catalogue serves
 * either way; the alias would not have saved us.
 */
@Composable()
export class useClimateService extends Vue {
  @Inject()
  private readonly api!: ApiService;

  /**
   * The dataset -> variables vocabulary. Cheap and deterministic: no body, no
   * AOI, no Earth Engine call, so it answers on a cold start even when Earth
   * Engine is unavailable. Static per deploy — fetch once per session
   * (`useClimateCatalogueStore` does the caching).
   */
  public async getClimateDatasets(
    signal?: AbortSignal,
  ): Promise<ApiResponse<ClimateDatasetsResponse>> {
    return this.api.get<ClimateDatasetsResponse>(
      `${import.meta.env.VITE_GEE_API}/climate/datasets`,
      { signal },
    );
  }

  /**
   * Sample the requested dataset over `body.geojson` for the given date range
   * and variable set, aggregated to daily / monthly / yearly buckets.
   * Returns labels + aligned numeric series — caller maps onto the
   * `FeatureTimeSeries` `TimeSeriesSamples` shape.
   *
   * Cognito Bearer is attached by `ApiService.post` automatically; the
   * BE further caches results in DDB so repeat polygons hit cache.
   */
  public async getClimateTimeseries(
    body: ClimateTimeseriesRequest,
    signal?: AbortSignal,
  ): Promise<ApiResponse<ClimateTimeseriesResponse>> {
    return this.api.post<ClimateTimeseriesResponse>(
      `${import.meta.env.VITE_GEE_API}/climate/timeseries`,
      body,
      { signal },
    );
  }
}
